import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CreditCard, ToggleLeft, ToggleRight, FloppyDisk, Trash, Plus, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_LABELS = {
  card: "Card (Visa / Mastercard / Verve)",
  mobilemoneyghana: "Mobile Money — Ghana",
  mobilemoneyfranco: "Mobile Money — Francophone Africa",
  mobilemoneyrwanda: "Mobile Money — Rwanda",
  mobilemoneyuganda: "Mobile Money — Uganda",
  mobilemoneyzambia: "Mobile Money — Zambia",
  mpesa: "M-Pesa (Kenya)",
  banktransfer: "Bank transfer",
  ussd: "USSD",
  gpay: "Google Pay",
  applepay: "Apple Pay",
};

export default function PaymentsManager() {
  const [mode, setMode] = useState(null);
  const [methods, setMethods] = useState({});
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_address: "", from_name: "Conexus Foundation", use_tls: true });
  const [smtpStatus, setSmtpStatus] = useState({ password_set: false });
  const [testEmail, setTestEmail] = useState("");
  const [testKeys, setTestKeys] = useState({ public_key: "", secret_key: "", encryption_key: "", webhook_secret_hash: "" });
  const [liveKeys, setLiveKeys] = useState({ public_key: "", secret_key: "", encryption_key: "", webhook_secret_hash: "" });
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({ name: "", amount: 10, currency: "USD", interval: "monthly", request_quota: 10000, description: "" });
  const [msg, setMsg] = useState("");

  const fetchMode = useCallback(async () => {
    const r = await axios.get(`${API}/admin/payments/mode`, { withCredentials: true });
    setMode(r.data);
  }, []);
  const fetchMethods = useCallback(async () => {
    const r = await axios.get(`${API}/admin/payments/methods`, { withCredentials: true });
    setMethods(r.data || {});
  }, []);
  const fetchSmtp = useCallback(async () => {
    const r = await axios.get(`${API}/admin/email/config`, { withCredentials: true });
    setSmtp({ host: r.data.host || "", port: r.data.port || 587, username: r.data.username || "",
              password: "", from_address: r.data.from_address || "",
              from_name: r.data.from_name || "Conexus Foundation", use_tls: r.data.use_tls ?? true });
    setSmtpStatus({ password_set: r.data.password_set });
  }, []);

  const fetchPlans = useCallback(async () => {
    const r = await axios.get(`${API}/payments/plans`);
    setPlans(r.data?.plans || []);
  }, []);

  useEffect(() => { fetchMode(); fetchMethods(); fetchSmtp(); fetchPlans(); }, [fetchMode, fetchMethods, fetchSmtp, fetchPlans]);

  const toggleMethod = async (method) => {
    const next = { ...methods, [method]: !methods[method] };
    setMethods(next);
    try {
      await axios.put(`${API}/admin/payments/methods`, { [method]: next[method] }, { withCredentials: true });
      setMsg(`${METHOD_LABELS[method] || method}: ${next[method] ? "enabled" : "disabled"}`);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Toggle failed");
      setMethods(methods);
    }
  };

  const saveSmtp = async () => {
    try {
      const payload = { ...smtp };
      if (!payload.password) delete payload.password; // don't overwrite if blank
      await axios.put(`${API}/admin/email/config`, payload, { withCredentials: true });
      setMsg("SMTP config saved.");
      fetchSmtp();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    }
  };

  const sendTestEmail = async () => {
    setMsg("Sending test email…");
    try {
      const r = await axios.post(`${API}/admin/email/test`, { to: testEmail }, { withCredentials: true });
      if (r.data.ok) {
        setMsg(`Test email sent to ${r.data.sent_to || testEmail}. Check the inbox (and Spam folder).`);
      } else {
        setMsg(r.data.error || "Send failed. Check the SMTP config.");
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || "Send failed");
    }
  };

  const toggleMode = async () => {
    if (!mode) return;
    const next = mode.mode === "test" ? "live" : "test";
    try {
      await axios.put(`${API}/admin/payments/mode`, { mode: next }, { withCredentials: true });
      setMsg(`Switched to ${next.toUpperCase()} mode.`);
      fetchMode();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Switch failed");
    }
  };

  const saveKeys = async (env, keys) => {
    try {
      await axios.put(`${API}/admin/payments/keys/${env}`, keys, { withCredentials: true });
      setMsg(`${env.toUpperCase()} keys saved.`);
      fetchMode();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    }
  };

  const addPlan = async () => {
    try {
      await axios.post(`${API}/admin/payments/plans`, newPlan, { withCredentials: true });
      setMsg(`Plan '${newPlan.name}' created.`);
      setNewPlan({ name: "", amount: 10, currency: "USD", interval: "monthly", request_quota: 10000, description: "" });
      fetchPlans();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Create failed");
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await axios.delete(`${API}/admin/payments/plans/${id}`, { withCredentials: true });
    fetchPlans();
  };

  return (
    <div className="space-y-6" data-testid="payments-manager">
      <div>
        <h2 className="font-heading text-xl font-semibold mb-1">Payments</h2>
        <p className="text-xs text-stone-500 font-body">
          Manage Flutterwave keys, switch between Test ↔ Live environments, and create API subscription plans.
        </p>
      </div>

      {msg && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 text-emerald-300 text-xs px-3 py-2 rounded-lg" data-testid="pay-msg">{msg}</div>
      )}

      {/* Mode toggle */}
      {mode && (
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 flex items-center gap-4" data-testid="pay-mode-card">
          <CreditCard size={20} weight="duotone" className="text-emerald-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-white">Active mode: <span className={mode.mode === "live" ? "text-emerald-400" : "text-amber-400"}>{mode.mode.toUpperCase()}</span></p>
            <p className="text-xs text-stone-500">
              Test: {mode.test_configured ? "configured" : "missing"} · Live: {mode.live_configured ? "configured" : "missing"}
            </p>
          </div>
          <button
            onClick={toggleMode}
            className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 text-sm"
            data-testid="pay-toggle-mode"
          >
            {mode.mode === "test" ? <ToggleLeft size={28} weight="fill" /> : <ToggleRight size={28} weight="fill" />}
            Switch to {mode.mode === "test" ? "Live" : "Test"}
          </button>
        </div>
      )}

      <KeyCard env="test" keys={testKeys} setKeys={setTestKeys} onSave={() => saveKeys("test", testKeys)} masked={mode?.test_public_masked} />
      <KeyCard env="live" keys={liveKeys} setKeys={setLiveKeys} onSave={() => saveKeys("live", liveKeys)} masked={mode?.live_public_masked} />

      {/* Payment Method Toggles */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5" data-testid="pay-methods-card">
        <h3 className="text-sm font-heading font-medium text-white mb-2">Enabled Payment Methods</h3>
        <p className="text-xs text-stone-500 mb-3">Toggle which payment options appear in the Flutterwave checkout popup.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {Object.keys(METHOD_LABELS).map((m) => (
            <button key={m} onClick={() => toggleMethod(m)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors text-left ${
                methods[m] ? "bg-emerald-600/10 border border-emerald-600/30 text-emerald-300" : "bg-stone-800/40 border border-stone-700 text-stone-500"
              }`}
              data-testid={`pay-method-${m}`}>
              {methods[m] ? <ToggleRight size={16} weight="fill" /> : <ToggleLeft size={16} weight="fill" />}
              <span className="flex-1">{METHOD_LABELS[m]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* SMTP Email Config */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5" data-testid="pay-smtp-card">
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeSimple size={18} weight="duotone" className="text-emerald-400" />
          <h3 className="text-sm font-heading font-medium text-white">SMTP Email Service</h3>
          {smtpStatus.password_set && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400">configured</span>}
        </div>
        <p className="text-xs text-stone-500 mb-3">
          Used for password reset codes, payment receipts, and system notifications. Works with Gmail App Passwords, SendGrid, Mailgun, or any standard SMTP.
        </p>
        {smtpStatus.last_test_at && (
          <div
            className={`mb-3 text-xs px-3 py-2 rounded-lg border ${
              smtpStatus.last_test_ok
                ? "bg-emerald-900/30 border-emerald-700/40 text-emerald-300"
                : "bg-red-900/30 border-red-700/40 text-red-300"
            }`}
            data-testid="pay-smtp-last-test"
          >
            <span className="font-heading font-semibold uppercase tracking-wider text-[10px] mr-2">
              {smtpStatus.last_test_ok ? "Last test ✓" : "Last test ✗"}
            </span>
            <span className="opacity-80">
              {smtpStatus.last_test_ok
                ? `Sent to ${smtpStatus.last_test_to || ""} at ${new Date(smtpStatus.last_test_at).toLocaleString()}.`
                : smtpStatus.last_test_error || "Unknown error"}
            </span>
          </div>
        )}
        {smtp.host?.toLowerCase().includes("gmail") && (
          <div className="mb-3 text-[11px] text-amber-300 bg-amber-900/20 border border-amber-700/30 px-3 py-2 rounded-lg" data-testid="pay-smtp-gmail-help">
            <span className="font-heading font-semibold">Gmail setup:</span> requires 2-Step Verification enabled, then a 16-character App Password generated from
            {" "}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/apppasswords</a>. Paste it into the Password field exactly (spaces removed).
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input type="text" placeholder="SMTP host (smtp.gmail.com)" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono" data-testid="pay-smtp-host" />
          <input type="number" placeholder="Port (587)" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono" data-testid="pay-smtp-port" />
          <input type="text" placeholder="Username" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono" data-testid="pay-smtp-username" />
          <input type="password" placeholder={smtpStatus.password_set ? "Password (leave blank to keep)" : "Password / App Password"}
            value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono" data-testid="pay-smtp-password" />
          <input type="email" placeholder="From address (no-reply@conexusfoundation.io)" value={smtp.from_address}
            onChange={(e) => setSmtp({ ...smtp, from_address: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono col-span-2" data-testid="pay-smtp-from" />
          <input type="text" placeholder="From name (Conexus Foundation)" value={smtp.from_name}
            onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white" data-testid="pay-smtp-from-name" />
          <label className="flex items-center gap-2 text-xs text-stone-300 px-3 py-2">
            <input type="checkbox" checked={smtp.use_tls} onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })}
              data-testid="pay-smtp-tls" />
            Use STARTTLS (port 587 standard)
          </label>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button onClick={saveSmtp}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
            data-testid="pay-smtp-save">
            <FloppyDisk size={12} weight="bold" /> Save SMTP
          </button>
          <input type="email" placeholder="Test recipient email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 min-w-[180px] bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white" data-testid="pay-smtp-test-email" />
          <button onClick={sendTestEmail} disabled={!testEmail}
            className="bg-stone-700 hover:bg-stone-600 disabled:bg-stone-800 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
            data-testid="pay-smtp-test-btn">
            <PaperPlaneTilt size={12} weight="bold" /> Send test
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5" data-testid="pay-plans-card">
        <h3 className="text-sm font-heading font-medium text-white mb-3">API Subscription Plans</h3>
        <p className="text-xs text-stone-500 mb-4">Define paid tiers for developer API access. Users get 1000 free requests/month, then must subscribe.</p>

        <div className="space-y-2 mb-4" data-testid="pay-plans-list">
          {plans.map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-stone-800/50 rounded-lg px-3 py-2" data-testid={`pay-plan-${p.id}`}>
              <div className="flex-1">
                <p className="text-sm text-white font-medium">{p.name}</p>
                <p className="text-xs text-stone-500">{p.currency} {p.amount} / {p.interval} · {p.request_quota.toLocaleString()} requests</p>
              </div>
              <button onClick={() => deletePlan(p.id)} className="text-red-400 hover:bg-red-500/10 p-1.5 rounded" data-testid={`pay-plan-delete-${p.id}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          {plans.length === 0 && <p className="text-xs text-stone-600 italic">No plans yet.</p>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
          <input type="text" placeholder="Plan name" value={newPlan.name}
            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="pay-newplan-name" />
          <input type="number" placeholder="Amount" value={newPlan.amount}
            onChange={(e) => setNewPlan({ ...newPlan, amount: Number(e.target.value) })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="pay-newplan-amount" />
          <select value={newPlan.currency} onChange={(e) => setNewPlan({ ...newPlan, currency: e.target.value })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white" data-testid="pay-newplan-currency">
            {["USD", "NGN", "GHS", "KES", "ZAR"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="number" placeholder="Requests/month" value={newPlan.request_quota}
            onChange={(e) => setNewPlan({ ...newPlan, request_quota: Number(e.target.value) })}
            className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white col-span-2"
            data-testid="pay-newplan-quota" />
          <button onClick={addPlan} disabled={!newPlan.name || !newPlan.amount}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-700 text-white text-xs px-3 py-2 rounded-lg flex items-center justify-center gap-1"
            data-testid="pay-newplan-add">
            <Plus size={12} weight="bold" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

function KeyCard({ env, keys, setKeys, onSave, masked }) {
  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-2" data-testid={`pay-keys-${env}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-heading font-medium text-white">{env.toUpperCase()} Keys</h3>
        {masked && <span className="text-[10px] text-stone-500 font-mono">{masked}</span>}
      </div>
      {["public_key", "secret_key", "encryption_key", "webhook_secret_hash"].map((field) => (
        <input key={field} type="text" placeholder={field.replace("_", " ")}
          value={keys[field]} onChange={(e) => setKeys({ ...keys, [field]: e.target.value })}
          className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono"
          data-testid={`pay-keys-${env}-${field}`} />
      ))}
      <button onClick={onSave}
        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
        data-testid={`pay-keys-${env}-save`}>
        <FloppyDisk size={12} weight="bold" /> Save {env} keys
      </button>
    </div>
  );
}
